import React from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Image,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { BIZFLEX_README_DOCS_BASE } from "../../components/common/constants";

const ApiUnifiedPlatform = () => {
  const apiFeatures = [
    {
      icon: "/assets/automate.svg",
      title: "Automate payments and reconciliation",
      description:
        "Streamline your financial operations with automated payment processing and instant reconciliation.",
    },
    {
      icon: "/assets/wallet.svg",
      title: "Generate static or dynamic virtual accounts instantly",
      description:
        "Create unlimited virtual accounts for your customers with real-time account generation.",
    },
    {
      icon: "/assets/bills.svg",
      title: "Power airtime, internet, and utility bill payments",
      description:
        "Enable seamless bill payments across all major service providers in Nigeria.",
    },
    {
      icon: "/assets/send.svg",
      title: "Send instant payouts to any Nigerian bank account",
      description:
        "Fast, reliable transfers to all Nigerian banks with real-time processing.",
    },
    {
      icon: "/assets/manage.svg",
      title: "Manage user wallets and balances",
      description:
        "Full wallet infrastructure with balance management, transaction history, and more.",
    },
    {
      icon: "/assets/enable.svg",
      title: "Enable USD virtual accounts, cards, and payouts",
      description:
        "Accept and send international payments with full USD support and competitive rates.",
    },
  ];

  return (
    <Box
      bg="#ffffff"
      w="100%"
      py={{ base: "60px", md: "100px" }}
      px={{ base: "20px", md: "80px", lg: "120px" }}
    >
      <VStack spacing="60px" align="center" w="100%">
        {/* Top Section - Centered Badge, Heading, and Description */}
        <VStack spacing="24px" align="center" w="100%" maxW="800px">
          {/* Badge - Centered */}
          <Badge
            display="flex"
            alignItems="center"
            gap="8px"
            w="fit-content"
            bg="#F8FAFC"
            color="#2463EB"
            px="16px"
            py="8px"
            border="1px solid #93C5FD"
            borderRadius="20px"
            fontSize="12px"
            fontWeight={400}
          >
            <Image src="/assets/unified.svg" alt="unified" w="16px" h="16px" />
            Unified Platform
          </Badge>

          {/* Main Heading - Centered */}
          <Text
            fontSize={{ base: "32px", md: "48px" }}
            fontWeight={700}
            lineHeight={{ base: "38.4px", md: "57.6px" }}
            color="#0F172B"
            textAlign="center"
          >
            Build With One Unified API Suite
          </Text>

          {/* Description - Centered */}
          <Text
            fontSize={{ base: "14px", md: "18px" }}
            lineHeight={{ base: "21px", md: "27px" }}
            color="#64748B"
            textAlign="center"
            maxW="700px"
          >
            Simplify financial operations with powerful, modular APIs. Managing
            multiple financial partners is hard. BizFlex gives you everything you
            need to collect, disburse, and move money locally and globally — all
            from one secure platform.
          </Text>
        </VStack>

        {/* Middle Section */}
        <Flex
          align="stretch"
          gap="40px"
          w="100%"
          flexDir={{ base: "column", md: "row" }}
          justifyContent="space-between"
        >
          {/* Left Side - Gradient CTA Card */}
          <Box
            w={{ base: "100%", md: "45%" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src="/assets/infinite-posibilities.svg"
              alt="One API, Infinite Possibilities"
              w="100%"
              maxW="100%"
              h="auto"
              objectFit="contain"
            />
          </Box>

          {/* Right Side - Feature Cards */}
          <Box w={{ base: "100%", md: "52%" }}>
            <VStack align="stretch" spacing="16px" w="100%">
              {apiFeatures.map((feature, index) => (
                <Box
                  key={index}
                  bg="#F8FAFC"
                  borderRadius="12px"
                  p="20px"
                  border="1px solid rgba(15, 23, 43, 0.06)"
                >
                  <HStack align="flex-start" spacing="16px" w="100%">
                    <Box
                      minW="24px"
                      w="24px"
                      h="24px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mt="4px"
                      flexShrink={0}
                    >
                      <Image
                        src={feature.icon}
                        alt={feature.title}
                        w="24px"
                        h="24px"
                      />
                    </Box>
                    <VStack align="flex-start" spacing="4px" flex={1}>
                      <Text
                        fontSize={{ base: "14px", md: "16px" }}
                        fontWeight={600}
                        color="#0F172B"
                      >
                        {feature.title}
                      </Text>
                      <Text
                        fontSize={{ base: "13px", md: "14px" }}
                        color="#64748B"
                        lineHeight="1.5"
                      >
                        {feature.description}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        </Flex>

        {/* Bottom Section - Centered CTA Button */}
        <Flex justifyContent="center" w="100%">
          <Button
            as="a"
            href={BIZFLEX_README_DOCS_BASE}
            target="_blank"
            rel="noopener noreferrer"
            bg="#2463EB"
            borderRadius="10px"
            w={{ base: "100%", md: "350px" }}
            h="69px"
            py="18px"
            px="14px"
            color="#fff"
            fontWeight={500}
            fontSize="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="10px"
            _hover={{ bg: "#1a4fc7" }}
          >
            Explore the API Reference
            <Image
              src="/assets/book.svg"
              alt=""
              w="17px"
              h="17px"
            />
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default ApiUnifiedPlatform;
